import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Collapse,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import AuthApi from '../../utils/api/authApi';
import { buildSignupAttributionPayload } from '../../utils/attribution';
import { storeEmail, storeToken } from '../../utils/storage';

const initialMessages = {
    error: '',
    success: '',
};

const defaultPromoState = {
    status: 'idle',
    message: '',
    checkedCode: '',
};

const defaultCopy = {
    signupTitle: 'Регистрация',
    signupDescription: 'Для регистрации заполните все поля формы. На email будут приходить системные уведомления, поэтому важно, чтобы он был актуальный.',
    loginTitle: 'Вход в аккаунт',
    loginDescription: 'Войдите по email и паролю.',
    recoveryTitle: 'Сброс пароля',
    recoveryDescription: 'Укажите email. Мы отправим письмо со ссылкой для сброса пароля.',
    setPasswordTitle: 'Создайте пароль',
    setPasswordDescription: 'Финальный шаг активации: задайте пароль, и мы сразу авторизуем вас.',
    signupSuccessAlert: 'Письмо для активации отправлено, его доставка может занять до 2 минут. Перейдите по ссылке из email и создайте пароль.',
    signupSuccessHint: 'Если письмо не пришло, проверьте папку «Спам». Если письмо так и не появилось, можно запустить сброс пароля прямо здесь.',
    signupSuccessSecondaryAction: 'Зарегистрировать другой email',
    signupSuccessRecoveryAction: 'Сбросить пароль',
    signupSubmitLabel: 'Зарегистрироваться',
    signupSubmitPendingLabel: 'Отправляем письмо...',
    loginSubmitLabel: 'Войти',
    loginSubmitPendingLabel: 'Входим...',
    recoverySubmitLabel: 'Отправить ссылку',
    recoverySubmitPendingLabel: 'Отправляем...',
    setPasswordSubmitLabel: 'Создать пароль и войти',
    setPasswordSubmitPendingLabel: 'Сохраняем...',
    forgotPasswordLabel: 'Забыли пароль?',
    backToLoginLabel: 'Вернуться ко входу',
    promoExpandLabel: 'У меня есть промокод',
    promoCollapseLabel: 'Не использовать промокод',
    promoCheckLabel: 'Проверить',
    promoCheckPendingLabel: 'Проверяем...',
    termsLabel: 'Принимаю условия оферты и политику обработки персональных данных.',
};

const PASSWORD_RECOVERY_DELAY_MS = 2 * 60 * 1000;

const getAuthFieldSx = (hasError = false) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
        borderRadius: 3,
        ...(hasError ? {
            '& fieldset': {
                borderColor: (theme) => theme.palette.errorBright?.main || '#d32f2f',
                borderWidth: '2px',
            },
            '&:hover fieldset': {
                borderColor: (theme) => theme.palette.errorBright?.main || '#d32f2f',
            },
            '&.Mui-focused fieldset': {
                borderColor: (theme) => theme.palette.errorBright?.main || '#d32f2f',
            },
        } : {}),
    },
    ...(hasError ? {
        '& .MuiFormHelperText-root': {
            color: (theme) => theme.palette.errorBright?.main || '#d32f2f',
        },
        '& .MuiInputLabel-root.Mui-error': {
            color: (theme) => theme.palette.errorBright?.main || '#d32f2f',
        },
    } : {}),
});

const getNormalizedEmail = (value) => String(value || '').trim().toLowerCase();
const isValidEmail = (value) => /\S+@\S+\.\S+/.test(getNormalizedEmail(value));
const hasMinPasswordLength = (value) => String(value || '').length >= 8;
const hasPasswordDigit = (value) => /\d/.test(String(value || ''));
const hasPasswordSpecial = (value) => /[^A-Za-z0-9]/.test(String(value || ''));
const signupFieldLabels = {
    email: 'Email',
    name: 'Имя',
    phone: 'Телефон',
};

const getBackendErrorItems = (error) => {
    const detail = error?.detail ?? error?.data?.detail ?? error?.data?.errors ?? error?.errors;

    if (Array.isArray(detail)) return detail;
    if (detail && typeof detail === 'object') {
        return Object.entries(detail).map(([field, message]) => ({ field, message }));
    }
    if (typeof detail === 'string') {
        return [{ message: detail }];
    }

    return [];
};

const resolveBackendSignupErrors = (error) => {
    const fieldErrors = {};
    const generalMessages = [];

    getBackendErrorItems(error).forEach((item) => {
        const rawField = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : item?.field;
        const field = String(rawField || '').trim();
        const message = String(item?.msg || item?.message || item?.detail || item || '').trim();

        if (['email', 'name', 'phone'].includes(field)) {
            fieldErrors[field] = message || `Проверьте поле «${signupFieldLabels[field]}».`;
        } else if (message) {
            generalMessages.push(message);
        }
    });

    return {
        fieldErrors,
        message: generalMessages.join(' ') || error?.message || 'Проверьте данные регистрации.',
    };
};

const formatCountdown = (ms) => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

function AuthPanel({
    initialMode = 'signup',
    showRecoveryTab = true,
    setPasswordFlow = null,
    onClearSetPasswordFlow,
    onAuthenticated,
    copy: copyOverrides = {},
}) {
    const copy = useMemo(() => ({ ...defaultCopy, ...copyOverrides }), [copyOverrides]);
    const [mode, setMode] = useState(initialMode);

    const [signupName, setSignupName] = useState('');
    const [signupPhone, setSignupPhone] = useState('');
    const [signupEmail, setSignupEmail] = useState('');
    const [signupPromocode, setSignupPromocode] = useState('');
    const [signupAgreedToTerms, setSignupAgreedToTerms] = useState(false);
    const [signupErrors, setSignupErrors] = useState({});
    const [signupCompleted, setSignupCompleted] = useState(false);
    const [signupRecoveryAvailableAt, setSignupRecoveryAvailableAt] = useState(null);
    const [nowTs, setNowTs] = useState(Date.now());

    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    const [recoveryEmail, setRecoveryEmail] = useState('');

    const [setPasswordEmail, setSetPasswordEmail] = useState('');
    const [setPasswordValue, setSetPasswordValue] = useState('');

    const [promoExpanded, setPromoExpanded] = useState(false);
    const [promoState, setPromoState] = useState(defaultPromoState);
    const [promoLoading, setPromoLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState(initialMessages);

    const linkEmail = getNormalizedEmail(setPasswordFlow?.email);
    const linkHash = setPasswordFlow?.k || '';
    const isSetPasswordMode = Boolean(setPasswordFlow?.enabled && linkEmail && linkHash);

    useEffect(() => {
        if (isSetPasswordMode) {
            setMode('setPassword');
            setSetPasswordEmail(linkEmail);
            setRecoveryEmail(linkEmail);
            setLoginEmail(linkEmail);
        } else {
            setMode(initialMode);
        }
        setSignupCompleted(false);
        setSignupRecoveryAvailableAt(null);
        setMessages(initialMessages);
    }, [initialMode, isSetPasswordMode, linkEmail]);

    useEffect(() => {
        if (!signupRecoveryAvailableAt || signupRecoveryAvailableAt <= Date.now()) {
            return undefined;
        }

        const timer = window.setInterval(() => {
            setNowTs(Date.now());
        }, 1000);

        return () => window.clearInterval(timer);
    }, [signupRecoveryAvailableAt]);

    const resetSignupFieldError = (field) => {
        setSignupErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const showError = (message) => setMessages({ error: message, success: '' });
    const showSuccess = (message) => setMessages({ error: '', success: message });
    const passwordChecks = useMemo(() => {
        const password = String(setPasswordValue || '');
        return [
            { label: 'Не менее 8 символов', passed: hasMinPasswordLength(password) },
            { label: 'Хотя бы 1 цифра', passed: hasPasswordDigit(password) },
            { label: 'Хотя бы 1 спецсимвол, например: ! @ # $ %', passed: hasPasswordSpecial(password) },
        ];
    }, [setPasswordValue]);

    const validateSignupForm = () => {
        const nextErrors = {};
        const email = getNormalizedEmail(signupEmail);
        const name = String(signupName || '').trim();
        const phone = String(signupPhone || '').trim();

        if (!email) {
            nextErrors.email = 'Укажите email.';
        } else if (!isValidEmail(email)) {
            nextErrors.email = 'Укажите корректный email.';
        }

        if (!name) {
            nextErrors.name = 'Укажите имя.';
        }

        if (!phone) {
            nextErrors.phone = 'Укажите телефон.';
        }

        if (!signupAgreedToTerms) {
            nextErrors.agreedToTerms = 'Использование сервиса возможно только при полном согласии с условиями.';
        }

        return nextErrors;
    };

    const buildSignupErrorMessage = (errors) => {
        const missingFields = [];
        if (errors.email === 'Укажите email.') missingFields.push('email');
        if (errors.name === 'Укажите имя.') missingFields.push('имя');
        if (errors.phone === 'Укажите телефон.') missingFields.push('телефон');

        const result = [];
        if (missingFields.length > 0) {
            result.push(`Укажите: ${missingFields.join(', ')}.`);
        }

        Object.entries(errors).forEach(([key, value]) => {
            if (['email', 'name', 'phone'].includes(key) && missingFields.length > 0) return;
            result.push(value);
        });

        return result.join(' ');
    };

    const setModeSafe = (nextMode) => {
        setMessages(initialMessages);
        setSignupCompleted(false);
        setMode(nextMode);
        if (isSetPasswordMode && nextMode !== 'setPassword') {
            onClearSetPasswordFlow?.();
        }
    };

    const handleValidatePromocode = async () => {
        const code = String(signupPromocode || '').trim();
        if (code.length < 5) {
            setPromoState({
                status: 'error',
                message: 'Промокод не найден',
                checkedCode: '',
            });
            return;
        }

        setPromoLoading(true);
        setPromoState({
            status: 'checking',
            message: '',
            checkedCode: '',
        });

        try {
            const response = await AuthApi.validatePromocode(code);
            const discount = response?.data?.FirstOrderDiscount;

            if (typeof discount === 'number') {
                setPromoState({
                    status: 'success',
                    message: `Скидка на первый заказ ${discount}%`,
                    checkedCode: code,
                });
            } else {
                setPromoState({
                    status: 'error',
                    message: 'Промокод не найден',
                    checkedCode: '',
                });
            }
        } catch (_error) {
            setPromoState({
                status: 'error',
                message: 'Не удалось проверить промокод. Повторите попытку.',
                checkedCode: '',
            });
        } finally {
            setPromoLoading(false);
        }
    };

    const handleSignupSubmit = async () => {
        const email = getNormalizedEmail(signupEmail);
        const nextErrors = validateSignupForm();
        setSignupErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            showError(buildSignupErrorMessage(nextErrors));
            return;
        }

        setLoading(true);
        setMessages(initialMessages);

        try {
            await AuthApi.authSignUp({
                email,
                name: String(signupName || '').trim(),
                phone: String(signupPhone || '').trim(),
                promocode: signupPromocode.trim() || null,
                useProjectScopedLimits: true,
                ...buildSignupAttributionPayload(),
            });
            setSignupCompleted(true);
            setSignupRecoveryAvailableAt(Date.now() + PASSWORD_RECOVERY_DELAY_MS);
            setMessages(initialMessages);
        } catch (error) {
            if (error?.code === 409) {
                showError('Email уже зарегистрирован. Используйте вход или сброс пароля.');
            } else if (error?.code === 422) {
                const backendErrors = resolveBackendSignupErrors(error);
                if (Object.keys(backendErrors.fieldErrors).length > 0) {
                    setSignupErrors((prev) => ({ ...prev, ...backendErrors.fieldErrors }));
                }
                showError(backendErrors.message);
            } else {
                showError('Не удалось отправить письмо для активации. Повторите попытку или напишите на info@brpadvice.ru.');
            }
        } finally {
            setLoading(false);
        }
    };

    const completeAuthentication = async (response, fallbackEmail) => {
        const resolvedEmail = response?.userData?.email || fallbackEmail;
        if (resolvedEmail) {
            storeEmail(resolvedEmail);
        }
        if (response?.token) {
            storeToken(response.token);
        }
        await onAuthenticated?.(response, resolvedEmail);
    };

    const handleLoginSubmit = async () => {
        const email = getNormalizedEmail(loginEmail);
        if (!isValidEmail(email)) {
            showError('Укажите корректный email.');
            return;
        }
        if (!loginPassword) {
            showError('Укажите пароль.');
            return;
        }
        if (!hasMinPasswordLength(loginPassword)) {
            showError('Пароль небезопасный. Задайте новый пароль длиной не менее 8 символов.');
            return;
        }

        setLoading(true);
        setMessages(initialMessages);

        try {
            const response = await AuthApi.authSignIn({
                email,
                password: loginPassword,
            });
            await completeAuthentication(response, email);
        } catch (error) {
            if (error?.code === 403) {
                showError(error?.message || 'Аккаунт не активен. Мы отправили письмо со ссылкой на email.');
            } else if (error?.code === 401) {
                showError('Неверный email или пароль.');
            } else if (error?.code === 422) {
                showError(error?.message || 'Проверьте email и пароль.');
            } else {
                showError('Не удалось выполнить вход. Повторите попытку.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRecoverySubmit = async () => {
        const email = getNormalizedEmail(recoveryEmail);
        if (!isValidEmail(email)) {
            showError('Укажите корректный email.');
            return;
        }

        const isRecoveryLockedForRecentSignup = (
            signupRecoveryAvailableAt
            && getNormalizedEmail(signupEmail)
            && email === getNormalizedEmail(signupEmail)
            && signupRecoveryAvailableAt > Date.now()
        );
        if (isRecoveryLockedForRecentSignup) {
            showError(`Сброс пароля для этого email будет доступен через ${formatCountdown(signupRecoveryAvailableAt - Date.now())}.`);
            return;
        }

        setLoading(true);
        setMessages(initialMessages);

        try {
            await AuthApi.authPasswordRecoveryRequest({ email });
            showSuccess('Если аккаунт существует, мы отправили письмо со ссылкой для сброса пароля.');
        } catch (error) {
            if (error?.code === 429 || error?.code === 422) {
                showError(error?.message || 'Не удалось отправить письмо. Повторите попытку позже.');
            } else {
                showError('Не удалось отправить письмо. Повторите попытку позже.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSetPasswordSubmit = async () => {
        const email = getNormalizedEmail(setPasswordEmail || linkEmail);
        if (!isValidEmail(email)) {
            showError('В ссылке указан некорректный email.');
            return;
        }
        if (!hasMinPasswordLength(setPasswordValue) || !hasPasswordDigit(setPasswordValue) || !hasPasswordSpecial(setPasswordValue)) {
            showError('Пароль должен содержать не менее 8 символов, хотя бы 1 цифру и 1 спецсимвол.');
            return;
        }
        if (!linkHash) {
            showError('Ссылка недействительна или устарела.');
            return;
        }

        setLoading(true);
        setMessages(initialMessages);

        try {
            const response = await AuthApi.authPasswordSetByLink({
                email,
                password: setPasswordValue,
                k: linkHash,
            });
            await completeAuthentication(response, email);
        } catch (error) {
            if (error?.code === 403 || error?.code === 422) {
                showError(error?.message || 'Ссылка недействительна или устарела.');
            } else {
                showError('Не удалось установить пароль. Повторите попытку позже.');
            }
        } finally {
            setLoading(false);
        }
    };

    const headerTitle = useMemo(() => {
        if (mode === 'login') return copy.loginTitle;
        if (mode === 'recovery') return copy.recoveryTitle;
        if (mode === 'setPassword') return copy.setPasswordTitle;
        return copy.signupTitle;
    }, [copy, mode]);

    const headerDescription = useMemo(() => {
        if (mode === 'login') return copy.loginDescription;
        if (mode === 'recovery') return copy.recoveryDescription;
        if (mode === 'setPassword') return copy.setPasswordDescription;
        return copy.signupDescription;
    }, [copy, mode]);

    const tabsValue = mode === 'recovery' && !showRecoveryTab ? 'login' : mode;
    const recoveryCountdownMs = signupRecoveryAvailableAt ? Math.max(0, signupRecoveryAvailableAt - nowTs) : 0;
    const isSameEmailAsRecentSignup = getNormalizedEmail(recoveryEmail) === getNormalizedEmail(signupEmail);
    const isRecoveryLockedForRecentSignup = Boolean(
        signupRecoveryAvailableAt
        && recoveryCountdownMs > 0
        && isSameEmailAsRecentSignup
        && getNormalizedEmail(signupEmail)
    );
    return (
        <Stack spacing={2.5}>
            <Box>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#101828', lineHeight: 1.15 }}>
                    {headerTitle}
                </Typography>
                <Typography sx={{ mt: 1, color: '#667085', lineHeight: 1.7 }}>
                    {headerDescription}
                </Typography>
            </Box>

            {!isSetPasswordMode ? (
                <Tabs value={tabsValue} onChange={(_event, nextMode) => setModeSafe(nextMode)} sx={{ mb: 0.5 }}>
                    <Tab value="signup" label="Регистрация" />
                    <Tab value="login" label="Вход" />
                    {showRecoveryTab ? <Tab value="recovery" label="Сброс пароля" /> : null}
                </Tabs>
            ) : null}

            {messages.error ? <Alert severity="error">{messages.error}</Alert> : null}
            {messages.success ? <Alert severity="success">{messages.success}</Alert> : null}

            {mode === 'signup' && signupCompleted ? (
                <Stack spacing={2.5}>
                    <Alert severity="success">{copy.signupSuccessAlert}</Alert>
                    <Typography sx={{ color: '#667085', lineHeight: 1.7 }}>
                        {copy.signupSuccessHint}
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            disabled={recoveryCountdownMs > 0}
                            onClick={() => {
                                setRecoveryEmail(signupEmail);
                                setModeSafe('recovery');
                            }}
                            sx={{
                                borderRadius: 999,
                                px: 3,
                                py: 1.25,
                                textTransform: 'none',
                                fontWeight: 700,
                                backgroundColor: '#155EEF',
                            }}
                        >
                            {recoveryCountdownMs > 0
                                ? `${copy.signupSuccessRecoveryAction} через ${formatCountdown(recoveryCountdownMs)}`
                                : copy.signupSuccessRecoveryAction}
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => {
                                setSignupCompleted(false);
                                setMessages(initialMessages);
                            }}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                        >
                            {copy.signupSuccessSecondaryAction}
                        </Button>
                    </Stack>
                </Stack>
            ) : null}

            {mode === 'signup' && !signupCompleted ? (
                <Stack spacing={2}>
                    <TextField
                        label="Email"
                        required
                        type="email"
                        value={signupEmail}
                        onChange={(event) => {
                            setSignupEmail(event.target.value);
                            resetSignupFieldError('email');
                        }}
                        autoComplete="email"
                        fullWidth
                        error={Boolean(signupErrors.email)}
                        helperText={signupErrors.email || ' '}
                        sx={getAuthFieldSx(Boolean(signupErrors.email))}
                    />
                    <TextField
                        label="Имя"
                        required
                        value={signupName}
                        onChange={(event) => {
                            setSignupName(event.target.value);
                            resetSignupFieldError('name');
                        }}
                        autoComplete="name"
                        fullWidth
                        error={Boolean(signupErrors.name)}
                        helperText={signupErrors.name || ' '}
                        sx={getAuthFieldSx(Boolean(signupErrors.name))}
                    />
                    <TextField
                        label="Телефон"
                        required
                        value={signupPhone}
                        onChange={(event) => {
                            setSignupPhone(event.target.value);
                            resetSignupFieldError('phone');
                        }}
                        autoComplete="tel"
                        fullWidth
                        error={Boolean(signupErrors.phone)}
                        helperText={signupErrors.phone || ' '}
                        sx={getAuthFieldSx(Boolean(signupErrors.phone))}
                    />
                    <Box>
                        <Button
                            variant="text"
                            onClick={() => {
                                if (promoExpanded) {
                                    setPromoExpanded(false);
                                    setSignupPromocode('');
                                    setPromoState(defaultPromoState);
                                } else {
                                    setPromoExpanded(true);
                                }
                            }}
                            sx={{ px: 0, textTransform: 'none', fontWeight: 600 }}
                        >
                            {promoExpanded ? copy.promoCollapseLabel : copy.promoExpandLabel}
                        </Button>
                        <Collapse in={promoExpanded}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
                                <TextField
                                    label="Промокод"
                                    value={signupPromocode}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setSignupPromocode(value);
                                        setPromoState((prev) => ({
                                            status: prev.checkedCode === value.trim() ? prev.status : 'idle',
                                            message: prev.checkedCode === value.trim() ? prev.message : '',
                                            checkedCode: prev.checkedCode === value.trim() ? prev.checkedCode : '',
                                        }));
                                    }}
                                    fullWidth
                                    sx={getAuthFieldSx()}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleValidatePromocode}
                                    disabled={promoLoading}
                                    sx={{ minWidth: { sm: 140 } }}
                                >
                                    {promoLoading ? copy.promoCheckPendingLabel : copy.promoCheckLabel}
                                </Button>
                            </Stack>
                            {promoState.message ? (
                                <Alert severity={promoState.status === 'success' ? 'success' : 'warning'} sx={{ mt: 1.5 }}>
                                    {promoState.message}
                                </Alert>
                            ) : null}
                        </Collapse>
                    </Box>
                    <Box
                        component="label"
                        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, cursor: 'pointer' }}
                    >
                        <Checkbox
                            checked={signupAgreedToTerms}
                            onChange={(event) => {
                                setSignupAgreedToTerms(event.target.checked);
                                resetSignupFieldError('agreedToTerms');
                            }}
                            sx={{ p: 0, mt: 0.2 }}
                        />
                        <Typography sx={{ color: '#475467', fontSize: 14, lineHeight: 1.7 }}>
                            {copy.termsLabel}
                        </Typography>
                    </Box>
                    {signupErrors.agreedToTerms ? (
                        <Typography
                            sx={{
                                mt: -1,
                                fontSize: 13,
                                color: (theme) => theme.palette.errorBright?.main || '#d32f2f',
                            }}
                        >
                            {signupErrors.agreedToTerms}
                        </Typography>
                    ) : null}
                    <Button
                        variant="contained"
                        onClick={handleSignupSubmit}
                        disabled={loading}
                        sx={{
                            alignSelf: 'flex-start',
                            borderRadius: 999,
                            px: 3,
                            py: 1.25,
                            textTransform: 'none',
                            fontWeight: 700,
                            backgroundColor: '#155EEF',
                        }}
                    >
                        {loading ? copy.signupSubmitPendingLabel : copy.signupSubmitLabel}
                    </Button>
                </Stack>
            ) : null}

            {mode === 'login' ? (
                <Stack spacing={2}>
                    <TextField
                        label="Email"
                        type="email"
                        value={loginEmail}
                        onChange={(event) => setLoginEmail(event.target.value)}
                        autoComplete="email"
                        fullWidth
                        sx={getAuthFieldSx()}
                    />
                    <TextField
                        label="Пароль"
                        type="password"
                        value={loginPassword}
                        onChange={(event) => setLoginPassword(event.target.value)}
                        autoComplete="current-password"
                        fullWidth
                        sx={getAuthFieldSx()}
                    />
                    <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            onClick={handleLoginSubmit}
                            disabled={loading}
                            sx={{
                                borderRadius: 999,
                                px: 3,
                                py: 1.25,
                                textTransform: 'none',
                                fontWeight: 700,
                                backgroundColor: '#155EEF',
                            }}
                        >
                            {loading ? copy.loginSubmitPendingLabel : copy.loginSubmitLabel}
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => {
                                setModeSafe('recovery');
                                setRecoveryEmail(loginEmail);
                            }}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                        >
                            {copy.forgotPasswordLabel}
                        </Button>
                    </Stack>
                </Stack>
            ) : null}

            {mode === 'recovery' ? (
                <Stack spacing={2}>
                    <TextField
                        label="Email"
                        type="email"
                        value={recoveryEmail}
                        onChange={(event) => setRecoveryEmail(event.target.value)}
                        autoComplete="email"
                        fullWidth
                        sx={getAuthFieldSx()}
                    />
                    {isRecoveryLockedForRecentSignup ? (
                        <Alert severity="info">
                            Сброс пароля для этого email будет доступен через {formatCountdown(recoveryCountdownMs)}.
                        </Alert>
                    ) : null}
                    <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            onClick={handleRecoverySubmit}
                            disabled={loading || isRecoveryLockedForRecentSignup}
                            sx={{
                                borderRadius: 999,
                                px: 3,
                                py: 1.25,
                                textTransform: 'none',
                                fontWeight: 700,
                                backgroundColor: '#155EEF',
                            }}
                        >
                            {loading ? copy.recoverySubmitPendingLabel : copy.recoverySubmitLabel}
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => {
                                setModeSafe('login');
                                setLoginEmail(recoveryEmail);
                            }}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                        >
                            {copy.backToLoginLabel}
                        </Button>
                    </Stack>
                </Stack>
            ) : null}

            {mode === 'setPassword' ? (
                <Stack spacing={2}>
                    <TextField
                        label="Email"
                        type="email"
                        value={setPasswordEmail}
                        onChange={(event) => setSetPasswordEmail(event.target.value)}
                        fullWidth
                        InputProps={{ readOnly: Boolean(linkEmail) }}
                        sx={getAuthFieldSx()}
                    />
                    <TextField
                        label="Новый пароль"
                        type="password"
                        value={setPasswordValue}
                        onChange={(event) => setSetPasswordValue(event.target.value)}
                        autoComplete="new-password"
                        fullWidth
                        sx={getAuthFieldSx()}
                    />
                    <Stack spacing={0.75} sx={{ mt: -0.5 }}>
                        {passwordChecks.map((check) => (
                            <Stack key={check.label} direction="row" spacing={1} alignItems="center">
                                <CheckCircleOutlineRoundedIcon
                                    sx={{
                                        fontSize: 18,
                                        color: check.passed ? '#12B76A' : '#98A2B3',
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        color: check.passed ? '#027A48' : '#667085',
                                    }}
                                >
                                    {check.label}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                    <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            onClick={handleSetPasswordSubmit}
                            disabled={loading}
                            sx={{
                                borderRadius: 999,
                                px: 3,
                                py: 1.25,
                                textTransform: 'none',
                                fontWeight: 700,
                                backgroundColor: '#155EEF',
                            }}
                        >
                            {loading ? copy.setPasswordSubmitPendingLabel : copy.setPasswordSubmitLabel}
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => setModeSafe('login')}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                        >
                            {copy.backToLoginLabel}
                        </Button>
                    </Stack>
                </Stack>
            ) : null}
        </Stack>
    );
}

export default AuthPanel;
